import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiArrowLeftLight, PiCheckCircleLight, PiCaretRightLight, PiCircleNotchLight, PiFloppyDiskLight, PiSlidersHorizontalLight, PiTargetLight } from 'react-icons/pi';
import { jobAPI } from '../../services/api';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const asArray = (value) => Array.isArray(value) ? value : [];

const Panel = ({ title, icon: Icon, action, children }) => (
  <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sky-700">
            <Icon size={14} />
          </span>
          <h2 className="truncate font-display text-[17px] font-normal text-[#0b1f3a]">{title}</h2>
        </div>
        {action}
      </div>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const bands = [
  { band: 'A', label: 'Strong', range: '80-100', tone: 'border-emerald-100 bg-emerald-50 text-emerald-700' },
  { band: 'B', label: 'Qualified', range: '65-79', tone: 'border-sky-100 bg-sky-50 text-sky-700' },
  { band: 'C', label: 'Review', range: '50-64', tone: 'border-amber-100 bg-amber-50 text-amber-700' },
  { band: 'D', label: 'Weak', range: '0-49', tone: 'border-slate-200 bg-slate-50 text-slate-600' }
];

const NumberField = ({ label, value, onChange, min = 0, max = 100 }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{label}</span>
    <input
      type="number"
      min={min}
      max={max}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))}
      className={inputClass}
    />
  </label>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <span className="text-sm font-medium text-[#0b1f3a]">{label}</span>
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
    />
  </label>
);

const WeightRow = ({ item, onChange }) => (
  <div className="grid gap-3 border-b border-slate-100 py-3 last:border-0 lg:grid-cols-[minmax(0,1fr)_160px_90px] lg:items-center">
    <div className="min-w-0">
      <div className="text-sm font-medium text-[#0b1f3a]">{item.label || item.displayName || labelize(item.key)}</div>
      <div className="mt-1 text-xs leading-5 text-slate-500">{item.description || item.category || item.key}</div>
    </div>
    <div className="flex items-center gap-3">
      <input
        type="range"
        min="0"
        max="100"
        value={Number(item.weight ?? 0)}
        onChange={(event) => onChange({ ...item, weight: Number(event.target.value) })}
        className="w-full accent-sky-600"
      />
      <span className="w-10 text-right text-xs font-medium text-slate-500">{Number(item.weight ?? 0)}%</span>
    </div>
    <label className="flex items-center justify-end gap-2 text-xs font-medium text-slate-500">
      Enabled
      <input
        type="checkbox"
        checked={item.enabled !== false}
        onChange={(event) => onChange({ ...item, enabled: event.target.checked })}
        className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
      />
    </label>
  </div>
);

const MatchingBands = () => {
  const navigate = useNavigate();
  const [configuration, setConfiguration] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [dimensionWeights, setDimensionWeights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [microPoints, setMicroPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dimensions');

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await jobAPI.getMatchingConfiguration();
      const config = response.configuration || response;
      setConfiguration(config);
      setPreferences(config.preferences || {});
      setDimensionWeights(asArray(config.dimensionWeights));
      setCategories(asArray(config.categories));
      setMicroPoints(asArray(config.microPoints));
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load matching configuration'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const enabledDimensions = useMemo(() => dimensionWeights.filter((item) => item.enabled !== false), [dimensionWeights]);
  const preferredBands = asArray(preferences.preferredBands);

  const updateArrayItem = (items, setter, nextItem) => {
    setter(items.map((item) => (item.key === nextItem.key ? nextItem : item)));
  };

  const toggleBand = (band) => {
    const nextBands = preferredBands.includes(band)
      ? preferredBands.filter((item) => item !== band)
      : [...preferredBands, band];
    setPreferences((current) => ({ ...current, preferredBands: nextBands }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        preferences: {
          ...preferences,
          minimumScoreThreshold: Number(preferences.minimumScoreThreshold ?? 50),
          immediateMatchLimit: Number(preferences.immediateMatchLimit ?? 10),
          preferredBands: preferredBands.length ? preferredBands : ['A', 'B']
        },
        dimensionWeights,
        categories,
        microPoints
      };
      const response = await jobAPI.updateMatchingConfiguration(payload);
      const saved = response.configuration || response;
      setConfiguration(saved);
      setPreferences(saved.preferences || payload.preferences);
      setDimensionWeights(asArray(saved.dimensionWeights || payload.dimensionWeights));
      setCategories(asArray(saved.categories || payload.categories));
      setMicroPoints(asArray(saved.microPoints || payload.microPoints));
      toast.success('Matching configuration saved.');
    } catch (saveError) {
      toast.error(extractError(saveError, 'Failed to save matching configuration'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
            <button onClick={() => navigate('/candidates')} className="inline-flex items-center gap-1 hover:text-[#0b1f3a]">
              <PiArrowLeftLight size={12} /> Candidates
            </button>
            <PiCaretRightLight size={12} />
            <span className="font-medium text-[#0b1f3a]">Matching bands</span>
          </nav>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Matching policy</p>
              <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Candidate Match Bands</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Tune the employer-side configuration used by immediate matching, candidate ranking, score explanations, and pipeline defaults.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <PiCircleNotchLight className="animate-spin" size={13} /> : <PiFloppyDiskLight size={13} />}
                Save policy
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {bands.map((band) => (
            <button
              key={band.band}
              onClick={() => toggleBand(band.band)}
              className={`rounded-lg border p-4 text-left shadow-[0_12px_30px_rgba(15,23,42,0.035)] transition ${preferredBands.includes(band.band) ? band.tone : 'border-slate-200 bg-white text-slate-600'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-display text-2xl font-normal">{band.band}</span>
                {preferredBands.includes(band.band) && <PiCheckCircleLight size={14} />}
              </div>
              <div className="mt-2 text-sm font-medium">{band.label}</div>
              <div className="mt-1 text-xs opacity-80">{band.range}% score range</div>
            </button>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <Panel title="Preferences" icon={PiTargetLight}>
            <div className="space-y-4">
              <Toggle
                label="Immediate matching"
                checked={preferences.enableImmediateMatching !== false}
                onChange={(checked) => setPreferences((current) => ({ ...current, enableImmediateMatching: checked }))}
              />
              <Toggle
                label="Matching notifications"
                checked={preferences.matchingNotifications !== false}
                onChange={(checked) => setPreferences((current) => ({ ...current, matchingNotifications: checked }))}
              />
              <Toggle
                label="Auto-contact top matches"
                checked={preferences.autoContactTopMatches === true}
                onChange={(checked) => setPreferences((current) => ({ ...current, autoContactTopMatches: checked }))}
              />
              <NumberField
                label="Minimum score"
                value={preferences.minimumScoreThreshold ?? 50}
                onChange={(value) => setPreferences((current) => ({ ...current, minimumScoreThreshold: value }))}
              />
              <NumberField
                label="Immediate match limit"
                value={preferences.immediateMatchLimit ?? 10}
                min={1}
                max={50}
                onChange={(value) => setPreferences((current) => ({ ...current, immediateMatchLimit: value }))}
              />
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Source</div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  {configuration?.source === 'employer' ? 'Employer override' : 'Default configuration'}
                  {configuration?.updatedAt && <span className="block text-xs text-slate-400">Updated {new Date(configuration.updatedAt).toLocaleDateString()}</span>}
                </div>
              </div>
            </div>
          </Panel>

          <Panel
            title="Weights"
            icon={PiSlidersHorizontalLight}
            action={(
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                {[
                  ['dimensions', 'Dimensions'],
                  ['categories', 'Categories'],
                  ['microPoints', 'Micro-points']
                ].map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`h-8 rounded-md px-3 text-xs font-medium ${activeTab === id ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          >
            {loading ? (
              <div className="flex min-h-[260px] items-center justify-center text-sm text-slate-500">
                <PiCircleNotchLight className="mr-2 animate-spin" size={14} /> Loading configuration
              </div>
            ) : activeTab === 'dimensions' ? (
              <div>
                <div className="mb-4 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">
                  {enabledDimensions.length} enabled dimensions. Weights are normalized by the backend when matches are scored.
                </div>
                {dimensionWeights.map((item) => (
                  <WeightRow
                    key={item.key}
                    item={item}
                    onChange={(nextItem) => updateArrayItem(dimensionWeights, setDimensionWeights, nextItem)}
                  />
                ))}
              </div>
            ) : activeTab === 'categories' ? (
              <div>
                {categories.map((item) => (
                  <WeightRow
                    key={item.key}
                    item={item}
                    onChange={(nextItem) => updateArrayItem(categories, setCategories, nextItem)}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                      <th className="py-3 pr-4">Signal</th>
                      <th className="py-3 pr-4">Category</th>
                      <th className="py-3 pr-4">Method</th>
                      <th className="py-3 pr-4">Weight</th>
                      <th className="py-3 text-right">Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {microPoints.map((item) => (
                      <tr key={item.key} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 pr-4">
                          <div className="text-sm font-medium text-[#0b1f3a]">{item.displayName || labelize(item.key)}</div>
                          <div className="mt-1 max-w-xl text-xs leading-5 text-slate-500">{item.description || item.key}</div>
                        </td>
                        <td className="py-3 pr-4 text-sm text-slate-600">{labelize(item.category)}</td>
                        <td className="py-3 pr-4 text-sm text-slate-600">{labelize(item.scoringMethod)}</td>
                        <td className="py-3 pr-4">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={item.weight ?? 0}
                            onChange={(event) => updateArrayItem(microPoints, setMicroPoints, { ...item, weight: Number(event.target.value) })}
                            className={`${inputClass} w-24`}
                          />
                        </td>
                        <td className="py-3 text-right">
                          <input
                            type="checkbox"
                            checked={item.enabled !== false}
                            onChange={(event) => updateArrayItem(microPoints, setMicroPoints, { ...item, enabled: event.target.checked })}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <Panel title="Scoring Surface" icon={PiSlidersHorizontalLight}>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              ['Preferred bands', preferredBands.join(', ') || 'A, B'],
              ['Minimum score threshold', `${preferences.minimumScoreThreshold ?? 50}%`],
              ['Active micro-points', microPoints.filter((point) => point.enabled !== false).length]
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
                <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{value}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default MatchingBands;
